import React, { useEffect, useMemo, useRef, useState } from "react"

import { css } from "@emotion/react"
import { AutoComplete, type RefSelectProps, Spin } from "antd"
import awesomeDebouncePromise from "awesome-debounce-promise"
import type {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormUnregister,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types"

import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { LookupHelper } from "../helpers/lookup.helper"
import { useScrollErroredInputIntoView } from "../hooks/scrollErroredInputtIntoView.hook"
import { FloatingLabelElement } from "./floatingLabel.element"
import { InputErrorElement } from "./inputError.element"
import { PrimaryText } from "./primaryText.element"

export const AutoCompleteElement: React.FunctionComponent<
    IAutoCompleteElementProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)
    const selectRef = useRef<RefSelectProps>(null)

    useScrollErroredInputIntoView(
        props.name,
        containerRef,
        props.reactHookFormErrors,
        props.ignoreScrollIntoViewOnError,
    )

    const [options, setOptions] = useState<ISelectRenderedOption[]>([])
    const [currentSearch, setCurrentSearch] = useState<string>()
    const [currentDisplayValue, setCurrentDisplayValue] = useState<string>()
    const [isAutoSelectionOnBlurEnabled, setIsAutoSelectionOnBlurEnabled] =
        useState<boolean>(false)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [preFetchedOptions, setPreFetchedOptions] = useState<boolean>(false)

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const isLabelFloated = useMemo<boolean>(() => {
        return props.isLabelStatic || !!currentDisplayValue || isFocused
    }, [currentDisplayValue, isFocused, props.isLabelStatic])

    const inputHasError = useMemo<boolean>(() => {
        return !!props.reactHookFormErrors?.[props.name]
    }, [props.reactHookFormErrors?.[props.name]])

    const debouncedFetcher = useMemo(() => {
        if (!props.dataFetcher) {
            return null
        }

        return awesomeDebouncePromise(
            props.dataFetcher,
            props.debounceMilliseconds || 500,
        )
    }, [props.dataFetcher])

    // Preload data while unfocused - so it's shown right away when focused
    // This should happen only once
    useEffect(() => {
        if (debouncedFetcher && props.searchOnFocus && !preFetchedOptions) {
            setPreFetchedOptions(true)

            void onSearch(currentSearch, false)
        }
    }, [debouncedFetcher, props.searchOnFocus, preFetchedOptions])

    useEffect(() => {
        if (props.reactHookFormRegister) {
            props.reactHookFormRegister(
                elementFullName,
                props.validations || {},
            )
        }

        return () => {
            props.reactHookFormUnregister?.(elementFullName)
        }
    }, [props.reactHookFormRegister])

    // Propagate default value to useForm values
    useEffect(() => {
        if (!props.defaultValue || !props.defaultValuePropagate) {
            return
        }

        onSelected(
            JSON.stringify({
                value: props.defaultValue,
            }),
        )
    }, [props.defaultValuePropagate, props.defaultValue])

    useEffect(() => {
        // Only set options from renderedOptions if no search is active
        // (to avoid overriding filtered options)
        if (!props.renderedOptions || currentSearch) {
            return
        }

        setOptions(props.renderedOptions)
    }, [props.renderedOptions, currentSearch])

    // Watch for external value or default value and update current display value
    useEffect(() => {
        if (!props.externalValue && !props.defaultValue) {
            return
        }

        setCurrentDisplayValue(props.externalValue || props.defaultValue)
    }, [props.externalValue, props.defaultValue])

    const notFoundContent: React.JSX.Element | null = useMemo(
        () =>
            LookupHelper.getNotFoundContent(
                props.notFoundTitle,
                props.notFoundSubtitle,
                props.notFoundAction,
                currentSearch,
                !props.ignoreBlurOnNotFoundAction && selectRef.current
                    ? selectRef.current
                    : undefined,
            ),

        [
            props.notFoundTitle,
            props.notFoundSubtitle,
            props.notFoundAction,
            currentSearch,
            selectRef.current,
            props.ignoreBlurOnNotFoundAction,
        ],
    )

    const onSearch = async (search = "", forceDisplayValueSet = true) => {
        if (props.clearOnEmptySearch && !search) {
            onClear()
        }

        if (forceDisplayValueSet || search) {
            setCurrentDisplayValue(search)
        }

        if (debouncedFetcher) {
            const searchedItems = await debouncedFetcher(search)

            const options = searchedItems.length
                ? searchedItems
                : props.getAdditionalItems
                  ? props.getAdditionalItems(search)
                  : []

            setOptions(options.map(item => props.itemRenderer(item)))
        } else if (props.renderedOptions) {
            // Filter renderedOptions when no dataFetcher is provided
            const filteredOptions = search
                ? props.renderedOptions.filter(option =>
                      option.displayValue
                          .toLowerCase()
                          .includes(search.toLowerCase()),
                  )
                : props.renderedOptions

            setOptions(filteredOptions)
        }

        if (props.onChange) {
            props.onChange(search)
        }

        setCurrentSearch(search)

        // When search is changed -> enabled auto selection of option on blur
        setIsAutoSelectionOnBlurEnabled(!props.ignoreAutoSelectionOnBlur)
    }

    const onSelected = (selectedOptionStringified: string) => {
        let selectedOption: ISelectRenderedOption | undefined

        try {
            selectedOption = JSON.parse(selectedOptionStringified)
        } catch (e) {
            selectedOption = undefined
        }

        if (!selectedOption) {
            return
        }

        // When option explicitly selected -> disable auto selection on blur
        setIsAutoSelectionOnBlurEnabled(false)

        if (props.onSelected) {
            props.onSelected(selectedOption.value)
        }

        if (props.reactHookFormSet) {
            props.reactHookFormSet(elementFullName, selectedOption.value, {
                shouldValidate: !!props.shouldValidate,
            })
        }

        setCurrentDisplayValue(
            selectedOption.displayValue || selectedOption.value,
        )
    }

    const onClear = () => {
        if (props.disallowClear) {
            return
        }

        if (props.onSelected) {
            props.onSelected(undefined)
        }

        if (props.reactHookFormSet) {
            props.reactHookFormSet(elementFullName, undefined, {
                shouldValidate: !!props.shouldValidate,
            })
        }
    }

    // On blur select first available option if our local state indicator allows this
    const onBlur = () => {
        setIsFocused(false)

        props.onBlur?.()

        if (
            !isAutoSelectionOnBlurEnabled ||
            !options.length ||
            !currentDisplayValue
        ) {
            return
        }

        onSelected(JSON.stringify(options[0]))
    }

    return (
        <div
            ref={containerRef}
            className={`
                relative
                ${props.className}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    isFloated={isLabelFloated}
                    withRequiredStar={
                        props.withRequiredStar && props.validations?.required
                    }
                    tooltip={props.tooltip}
                    inputHasPrefix={!!props.inputPrefix}
                />
            )}

            <div className="relative">
                {props.inputPrefix ? (
                    <div
                        className="vertical-center"
                        css={[styles.inputPrefixWrapper]}
                    >
                        {typeof props.inputPrefix === "string" ? (
                            <img
                                src={props.inputPrefix}
                                alt={props.label}
                                css={styles.inputPrefixIcon}
                            />
                        ) : (
                            props.inputPrefix
                        )}
                    </div>
                ) : null}

                <AutoComplete
                    ref={selectRef}
                    size={props.size || "large"}
                    placeholder={
                        isLabelFloated || !props.label
                            ? props.placeholder
                            : undefined
                    }
                    css={[styles.autoComplete]}
                    className={`
                        w-full
                        ${
                            props.inputPrefix
                                ? "with-prefix-icon"
                                : props.inputSuffix
                                  ? "with-suffix-icon"
                                  : ""
                        }
                        ${
                            inputHasError &&
                            (!props.hideErrors || props.forceHighlightOnError)
                                ? "errored"
                                : ""
                        }
                    `}
                    disabled={props.disabled}
                    defaultValue={props.defaultValue}
                    onSearch={search => {
                        void onSearch(search)
                    }}
                    onSelect={onSelected}
                    onClear={onClear}
                    dropdownMatchSelectWidth={false}
                    allowClear={!props.disallowClear}
                    value={currentDisplayValue}
                    notFoundContent={
                        currentSearch && notFoundContent
                            ? notFoundContent
                            : undefined
                    }
                    onFocus={() => {
                        setIsFocused(true)
                        props.onFocus?.()

                        if (
                            props.searchOnFocus &&
                            !currentDisplayValue?.length
                        ) {
                            void onSearch(currentSearch, false)
                        }
                    }}
                    onBlur={onBlur}
                >
                    {options.map(item => (
                        <AutoComplete.Option
                            key={
                                item.key ||
                                item.value.id ||
                                item.dropdownDisplayValue ||
                                item.displayValue
                            }
                            value={JSON.stringify(item)}
                        >
                            <div css={styles.optionWrapper}>
                                <PrimaryText typography="text">
                                    {item.dropdownDisplayValue ||
                                        item.displayValue}
                                </PrimaryText>

                                {item.dropdownDisplayValueAdditional ? (
                                    <div className="flex-1">
                                        <PrimaryText
                                            css={styles.optionWrapper}
                                            typography="caption"
                                        >
                                            {typeof item.dropdownDisplayValueAdditional ===
                                            "string"
                                                ? item.dropdownDisplayValueAdditional
                                                      .split("<br />")
                                                      .join("\n")
                                                : item.dropdownDisplayValueAdditional}
                                        </PrimaryText>
                                    </div>
                                ) : null}
                            </div>
                        </AutoComplete.Option>
                    ))}
                </AutoComplete>

                {props.inputSuffix ? (
                    <div
                        className="vertical-center"
                        css={[styles.inputSuffixWrapper]}
                    >
                        {typeof props.inputSuffix === "string" ? (
                            <img
                                src={props.inputSuffix}
                                alt={props.label}
                                css={styles.inputSuffixIcon}
                            />
                        ) : (
                            props.inputSuffix
                        )}
                    </div>
                ) : null}

                {props.isLoading ? (
                    <div
                        className="vertical-center"
                        css={[styles.spinnerWrapper]}
                    >
                        <Spin size="small" />
                    </div>
                ) : null}
            </div>

            {inputHasError &&
                !props.hideErrors &&
                props.reactHookFormErrors && (
                    <InputErrorElement
                        text={
                            (
                                props.reactHookFormErrors[
                                    props.name
                                ] as FieldError
                            ).message || "Error"
                        }
                    />
                )}
        </div>
    )
}

const styles = {
    inputPrefixWrapper: css({
        position: "absolute",
        left: 14,
        zIndex: 10,
    }),
    inputSuffixWrapper: css({
        position: "absolute",
        right: 11,
        zIndex: 10,
    }),
    inputPrefixIcon: css({
        width: 16,
    }),
    inputSuffixIcon: css({
        width: 16,
    }),
    autoComplete: css({
        borderRadius: 6,
    }),
    optionWrapper: css({
        marginTop: 4,
    }),
    spinnerWrapper: css({
        position: "absolute",
        right: 40,
        marginTop: 3,
    }),
}

interface IAutoCompleteElementProps {
    name: string
    fieldGroupName?: string
    className?: string
    label?: string
    isLabelStatic?: boolean
    withRequiredStar?: boolean
    externalValue?: string
    onChange?: (search: string) => void
    onSelected?: (item: any) => void
    dataFetcher?: (search?: string) => Promise<any[]> | any[]
    getAdditionalItems?: (search?: string) => any[]
    itemRenderer: (item: any) => ISelectRenderedOption
    defaultValue?: any
    defaultValuePropagate?: boolean
    placeholder?: string
    disabled?: boolean
    disallowClear?: boolean
    clearOnEmptySearch?: boolean
    size?: "large" | "small"
    inputPrefix?: string | React.JSX.Element
    inputSuffix?: string | React.JSX.Element
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookFormErrors?: DeepMap<FieldValues, FieldError>
    hideErrors?: boolean
    forceHighlightOnError?: boolean
    validations?: IIndexable // Validations object
    renderedOptions?: ISelectRenderedOption[]
    shouldValidate?: boolean
    notFoundTitle?: string
    notFoundSubtitle?: string
    notFoundAction?: (currentInputValue?: string) => React.JSX.Element | null
    ignoreBlurOnNotFoundAction?: boolean
    isLoading?: boolean
    onFocus?: () => void
    onBlur?: () => void
    ignoreAutoSelectionOnBlur?: boolean
    debounceMilliseconds?: number
    tooltip?: string
    searchOnFocus?: boolean
    ignoreScrollIntoViewOnError?: boolean
}
